"use client";

import * as m from "motion/react-m";
import { useTranslations } from "next-intl";

import { EventName } from "@mw/shared/analyticsEventNames";
import { useNativeAudio } from "@/app/_providers/native-audio";
import { publicUrls } from "@/constants";
import { Button } from "@components/core/Button";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { DiscPlayer } from "@ui/components/core/Disc/DiscPlayer";
import { sizes } from "@ui/lib/sizes";
import { pxToRem } from "@ui/lib/utils";

import imgOriginalBG from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 05.png";
import imgIsolatedBG from "@/app/_blocks/BlockVoicesSlider/images/Voice BG 04.png";

import { SideBySideOmniboxLayout, ViewLayout } from "@ui/components/core/SideBySideOmnibox";
import type { UseVoiceIsolateReturn } from "./hooks/useVoiceIsolate";

const DISC_SIZES = sizes(pxToRem(128), { md: pxToRem(160) });

interface IsolationResultViewProps {
  voiceIsolateData: UseVoiceIsolateReturn;
}

export default function IsolationResultView({ voiceIsolateData }: IsolationResultViewProps) {
  const t = useTranslations("ClientKeys.VoiceIsolatorV2");
  const { playingId, togglePlay } = useNativeAudio();
  const { originalAudioUrl, isolatedAudioUrl, restart } = voiceIsolateData;

  const handlePlayOriginal = () => {
    if (!originalAudioUrl) return;
    togglePlay({
      id: originalAudioUrl,
      url: originalAudioUrl,
      loop: false,
      trackingProps: { source: "voice_isolator_v2", audio_type: "original" },
    });
  };

  const handlePlayIsolated = () => {
    if (!isolatedAudioUrl) return;
    togglePlay({
      id: isolatedAudioUrl,
      url: isolatedAudioUrl,
      loop: false,
      trackingProps: { source: "voice_isolator_v2", audio_type: "isolated" },
    });
  };

  return (
    <m.div
      className='tw-flex tw-size-full tw-flex-1 tw-flex-col'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
    >
      <ViewLayout
        footer={
          <div className='tw-flex tw-size-full tw-items-center tw-justify-center md:tw-justify-between'>
            <m.p
              className='tw-hidden md:tw-block tw-text-black/60 tw-max-w-sm tw-text-2xs lg:tw-text-sm tw-line-clamp-2'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15 }}
            >
              {t("result_description")}
            </m.p>
            <div className='tw-flex tw-items-center tw-gap-2'>
              <LinkTracked
                asButton
                buttonVariants={{ variant: "primary", size: "lg" }}
                href={publicUrls.signUp}
                eventName={EventName.mw_voice_isolator_sign_up_clicked}
                eventProperties={{ source_component: "voice_isolator_v2_result" }}
                className='tw-text-2xs sm:tw-text-base'
              >
                {t("sign_up")}
              </LinkTracked>
              <Button onClick={restart} variant='elevated' size='lg' className='tw-text-2xs sm:tw-text-base'>
                {t("try_another")}
              </Button>
            </div>
          </div>
        }
      >
        <SideBySideOmniboxLayout
          leftLabel={t("original_audio")}
          rightLabel={t("isolated_voice")}
          leftContent={
            <DiscPlayer
              className='tw-ring-[0.5px] tw-ring-inset tw-ring-black/10'
              isPlaying={playingId === originalAudioUrl}
              onClick={handlePlayOriginal}
              image={{ src: imgOriginalBG, alt: "", sizes: DISC_SIZES }}
            />
          }
          rightContent={
            <m.div layoutId='isolate-disc' className='tw-size-full'>
              <DiscPlayer
                className='tw-ring-[0.5px] tw-ring-inset tw-ring-black/10'
                isPlaying={playingId === isolatedAudioUrl}
                onClick={handlePlayIsolated}
                image={{ src: imgIsolatedBG, alt: "", sizes: DISC_SIZES }}
              />
            </m.div>
          }
        />
      </ViewLayout>
    </m.div>
  );
}
